/**
 * Uploading an applicant's own ID / income documents (COL-533).
 *
 * Lifted verbatim out of V1's `uploadDocumentsMutation` in `Dashboard.tsx` so
 * the V2 documents card can offer the same "add a file" without becoming a
 * SECOND writer to `tenant_documents`. `useTenantDocumentSummary` was written
 * read-only precisely to avoid that, and its header says why: a second writer
 * is how the two surfaces drift. One function, both callers.
 *
 * Behaviour is V1's, unchanged: per-file validation, a per-file error rather
 * than an aborted batch, and the stored file removed again if its row fails to
 * insert. The only edit is that the limits are named constants instead of
 * inline literals, so the UI can state them before a file is rejected.
 *
 * Note on `file_url`: this writes `getPublicUrl`, which no longer resolves —
 * the `contracts` bucket went private in COL-436. It is kept because every
 * reader parses the bucket and path back out of that string and signs a fresh
 * URL (`resolveStoredFileUrl`), and changing the stored shape would break them
 * all. Not this change's problem to fix.
 */

import { supabase } from "@/integrations/supabase/client";

/** The two kinds the applicant pack asks for. V1 uses the same literals. */
export type TenantDocumentType = "id_passport" | "proof_of_income";

export const IDENTITY_DOCUMENT_TYPE: TenantDocumentType = "id_passport";
export const INCOME_DOCUMENT_TYPE: TenantDocumentType = "proof_of_income";

/** V1's limit, preserved. */
export const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024;

/** V1's list, preserved. */
export const ACCEPTED_DOCUMENT_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
] as const;

/** For an `<input accept>`, derived so the two cannot disagree. */
export const DOCUMENT_ACCEPT_ATTRIBUTE = ACCEPTED_DOCUMENT_TYPES.join(",");

export type DocumentUploadError = { fileName: string; error: string };

export type DocumentUploadResult = {
  uploaded: unknown[];
  errors?: DocumentUploadError[];
};

export type UploadTenantDocumentsInput = {
  files: File[];
  documentType: TenantDocumentType;
  bookingId?: number;
  userId: string;
  /**
   * Wording for the one message this function invents rather than relays.
   *
   * V1 passed `t("dashboard.docs.unknownError", …)` here; lifting the loop out
   * of the component would otherwise have hard-coded English into a path both
   * dashboards use. Optional so a caller with no translator still gets a
   * sentence rather than an empty string.
   */
  unknownErrorLabel?: string;
};

/**
 * Store each file and record it, collecting per-file failures.
 *
 * Throws only when EVERY file failed — a partial batch returns what landed
 * plus what did not, which is what V1's callers already render.
 */
export async function uploadTenantDocuments({
  files,
  documentType,
  bookingId,
  userId,
  unknownErrorLabel = "Unknown error",
}: UploadTenantDocumentsInput): Promise<DocumentUploadResult> {
  if (!userId) throw new Error("User not authenticated");
  if (files.length === 0) throw new Error("No files provided");

  const uploaded: unknown[] = [];
  const errors: DocumentUploadError[] = [];

  for (const file of files) {
    try {
      if (file.size > MAX_DOCUMENT_BYTES) {
        errors.push({
          fileName: file.name,
          error: "File exceeds maximum size of 10MB",
        });
        continue;
      }
      if (!(ACCEPTED_DOCUMENT_TYPES as readonly string[]).includes(file.type)) {
        errors.push({
          fileName: file.name,
          error: "File must be a PDF or image (JPEG, PNG, WebP)",
        });
        continue;
      }

      const fileExt = file.name.split(".").pop() || "pdf";
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(7);
      const storagePath = `${documentType}/${userId}/${timestamp}-${randomId}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("contracts")
        .upload(storagePath, file, {
          contentType: file.type,
          upsert: false,
        });
      if (uploadError) {
        errors.push({ fileName: file.name, error: uploadError.message });
        continue;
      }

      const { data: urlData } = supabase.storage
        .from("contracts")
        .getPublicUrl(storagePath);

      const documentRecord: Record<string, unknown> = {
        user_id: userId,
        document_type: documentType,
        title: file.name,
        file_url: urlData.publicUrl,
        file_name: file.name,
        file_size: file.size,
        mime_type: file.type,
        status: "pending",
        uploaded_by: userId,
      };
      if (bookingId) documentRecord.booking_id = bookingId;

      const { data: docData, error: docError } = await supabase
        .from("tenant_documents")
        .insert([documentRecord as never])
        .select()
        .single();

      if (docError) {
        // The row is the record; a stored file with no row is invisible to
        // every surface, so take it back out rather than leave it orphaned.
        await supabase.storage.from("contracts").remove([storagePath]);
        errors.push({ fileName: file.name, error: docError.message });
        continue;
      }

      uploaded.push(docData);
    } catch (error: unknown) {
      errors.push({
        fileName: file.name,
        error: (error as Error)?.message || unknownErrorLabel,
      });
    }
  }

  if (uploaded.length === 0 && errors.length > 0) {
    throw new Error(`All uploads failed: ${errors[0].error}`);
  }

  return { uploaded, errors: errors.length > 0 ? errors : undefined };
}

/**
 * May the TENANT remove this document themselves?
 *
 * No, once an admin has approved it. Other surfaces read the pack as complete
 * on the strength of an approved document, so letting the tenant take it back
 * out would un-complete a verified application behind the reviewer's back.
 * `delete_tenant_document` enforces this server-side; this is the matching
 * client rule, so the UI does not offer an action that will be refused.
 *
 * Staff are not covered here — admins may delete anything, and the screens
 * that let them do so are their own.
 */
export function canTenantDeleteDocument(doc: {
  status?: string | null;
}): boolean {
  return String(doc?.status ?? "").toLowerCase() !== "approved";
}
